import { faDoorOpen } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CircleButton, Switch, Tooltip, Typography, cn, formatNumber } from "@tosize/ui";
import type { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useCabinetStore } from "../../../../context/StoreContext";
import { AppModus, CompartmentModus, useCanvasStore } from "../../../../hooks/useCanvasStore";
import { useNavigation } from "../../../../hooks/useNavigation";
import TitleBlock from "../../../TitleBlock";
import { VERTICAL_BLIND_THRESHOLD, getCurrentThickness } from "../../../renderer/data";
import { MAX_DOUBLE_DOOR_WIDTH } from "../../../renderer/data/DoubleDoor";
import { Divider, DividerSmall } from "../../Divider";
import Navigation from "../Navigation";

export default function SingleCompartmentContent() {
	const { navigate } = useNavigation();
	const intl = useIntl();

	return (
		<div className="space-y-3">
			<Doors />
			<Divider />
			<Shelves />

			<Navigation
				prev={{
					onClick: () => navigate(AppModus.layout),
					title: intl.formatMessage({ defaultMessage: "Indeling aanpassen", id: "NdVfW3" }),
				}}
			/>
		</div>
	);
}

function Doors() {
	const intl = useIntl();
	const { api, state } = useCabinetStore();
	const current = getCurrentThickness(state, api);

	return (
		<div className="flex flex-col gap-y-1.5">
			<TitleBlock
				title={intl.formatMessage({ defaultMessage: "Deurtjes", id: "Tk9eQS" })}
				explanation={
					<>
						<Typography variant="paragraph">
							<FormattedMessage
								defaultMessage="Deurtjes zijn standaard voorzien van soft-close scharnieren en een push-to-open systeem."
								id="+XEo6K"
							/>
						</Typography>
						<Typography variant="paragraph-bold">
							<FormattedMessage defaultMessage="Afmetingen en extra informatie" id="/AncE7" />
						</Typography>
						<ul>
							<li>
								{intl.formatMessage(
									{
										defaultMessage:
											"Deurtjes zijn minimaal {min} breed en maximaal {max} breed (per deurtje)",
										id: "7cAeA5",
									},
									{
										min: formatNumber({
											lang: intl.locale,
											value: Math.ceil(Number(current?.parts.door.constraints.width.min) / 10),
											unit: "centimeter",
										}),
										max: formatNumber({
											lang: intl.locale,
											value: Number(current?.parts.door.constraints.width.max) / 10,
											unit: "centimeter",
										}),
									},
								)}
							</li>
							<li>
								{intl.formatMessage(
									{
										defaultMessage:
											"Vakken met deurtjes zijn minimaal {min} breed (enkel deurtje) en maximaal {max} breed (dubbele deurtjes).",
										id: "JzY1Pp",
									},
									{
										min: formatNumber({
											lang: intl.locale,
											value: Math.ceil(Number(current?.parts.door.constraints.width.min) / 10),
											unit: "centimeter",
										}),
										max: formatNumber({
											lang: intl.locale,
											value: Math.min(
												MAX_DOUBLE_DOOR_WIDTH / 10,
												Number(current?.parts.background.constraints.depth.max) / 10,
											),
											unit: "centimeter",
										}),
									},
								)}
							</li>
							<li>
								<FormattedMessage
									defaultMessage="Een deurtje moet hoger zijn dan de breedte (om doorhangen te voorkomen)"
									id="m6wwUO"
								/>
							</li>
							<li>
								{intl.formatMessage(
									{
										defaultMessage:
											"Bij een vak met dubbele deuren breder dan {value} wordt een blinde staander toegevoegd. Dit voorkomt doorhangen en klemmen van de deurtjes op de lange termijn.",
										id: "DNDuTc",
									},
									{
										value: formatNumber({
											lang: intl.locale,
											value: VERTICAL_BLIND_THRESHOLD / 10,
											unit: "centimeter",
										}),
									},
								)}
							</li>
						</ul>
						<Typography variant="paragraph">
							<FormattedMessage
								defaultMessage="Soms zal de configurator in verband met het bovenstaande de door jouw gewenste optie voor deurtjes niet weergeven. Om toch deurtjes te kunnen toevoegen kun je:"
								id="LH04hx"
							/>
						</Typography>
						<ol>
							<li>
								<FormattedMessage
									defaultMessage="Terug naar stap 2 om de indeling aan te passen. Splits vakken in tweeën door staanders of liggers toe te voegen of pas de vakverhoudingen aan zodat deurtje(s) wel kunnen"
									id="UWbDeG"
								/>
							</li>
							<li>
								<FormattedMessage
									defaultMessage="Voor bredere vakken kiezen voor dubbele deurtjes in plaats van enkele deurtjes"
									id="m559Qu"
								/>
							</li>
						</ol>
						<Typography variant="paragraph">
							<FormattedMessage
								defaultMessage="Als je een deurtje hebt toegevoegd verschijnt de knop {logo} waarmee je deurtjes van de kast open en dicht kan zetten."
								id="RhXSqW"
								values={{
									logo: <FontAwesomeIcon icon={faDoorOpen} />,
								}}
							/>
						</Typography>
					</>
				}
			/>

			<Item id={CompartmentModus.DoorLeft}>
				<FormattedMessage defaultMessage="Linksdraaiend" id="KUoYNV" />
			</Item>

			<DividerSmall />

			<Item id={CompartmentModus.DoorRight}>
				<FormattedMessage defaultMessage="Rechtsdraaiend" id="xCpY7D" />
			</Item>

			<DividerSmall />

			<Item id={CompartmentModus.DoorDouble}>
				<FormattedMessage defaultMessage="Dubbel" id="6nslmN" />
			</Item>

			<DividerSmall />

			<Item id={CompartmentModus.RemoveDoor}>
				<FormattedMessage defaultMessage="Verwijderen" id="mcAe/q" />
			</Item>
		</div>
	);
}

function Shelves() {
	const intl = useIntl();

	return (
		<div className="flex flex-col gap-y-1.5">
			<TitleBlock
				title={intl.formatMessage({ defaultMessage: "Legplanken", id: "Gt7JI0" })}
				explanation={
					<>
						<Typography variant="paragraph">
							<FormattedMessage
								defaultMessage="Legplanken zijn de horizontale planken die binnen het raamwerk van de staanders en liggers worden geplaatst. Ze zijn 18mm dik en je kan 1 of meerdere achter een deurtje plaatsen of in een open vak. Ze kunnen in hoogte versteld worden door middel van de rijboring om zo verschillende maten van opslagruimte te creëren."
								id="Knsz2I"
							/>
						</Typography>
						<Typography variant="paragraph">
							<FormattedMessage
								defaultMessage="Nadat je onderstaande optie toevoegen hebt aangezet lichten de vakken groen op waarin je legplanken toevoegen."
								id="90ojJ3"
							/>
						</Typography>
						<ul>
							<li>
								<FormattedMessage
									defaultMessage="Legplanken zijn minimaal 20,0 cm breed en maximaal 70,0 cm breed om doorhangen te voorkomen"
									id="AhHtA5"
								/>
							</li>
							<li>
								<FormattedMessage
									defaultMessage="Er kunnen 1 of meerdere legplanken in een vak"
									id="ksxIfH"
								/>
							</li>
							<li>
								<FormattedMessage
									defaultMessage="Het maximaal aantal legplanken per vak wordt bepaald door de beschikbare ruimte in het vak. Hier wordt ook met bijvoorbeeld scharnieren rekening gehouden"
									id="AuEdBL"
								/>
							</li>
							<li>
								<FormattedMessage
									defaultMessage="De verdeling van de legplanken kun je in de configurator niet aanpassen omdat je dit altijd nog kunt wijzigen door de rijboring"
									id="NHzsPk"
								/>
							</li>
							<li>
								<FormattedMessage
									defaultMessage="Op de kopse kanten van een legplank zal de rijboringen worden onderbroken. Hier kan je eventueel zelf een extra boorgat realiseren wanneer gewenst."
									id="q5eTdI"
								/>
							</li>
						</ul>
						<Typography variant="paragraph">
							<FormattedMessage
								defaultMessage="Let op! Kies je voor een legplank in een open vak (dus zonder deurtje)? Dan is de rijboring dus zichtbaar in dit open vak!"
								id="S7Of7m"
							/>
						</Typography>
						<Typography variant="paragraph">
							<FormattedMessage
								defaultMessage="Tip: Als je legplanken achter een deurtje hebt geplaatst, kan je met {logo} de deurtjes van de kast open zetten zodat je kan zien hoeveel legplanken er achter het deurtje zitten."
								id="zD1Og3"
								values={{
									logo: <FontAwesomeIcon icon={faDoorOpen} />,
								}}
							/>
						</Typography>
					</>
				}
			/>

			<Item id={CompartmentModus.AddShelf}>
				<FormattedMessage defaultMessage="Toevoegen" id="O0INX/" />
			</Item>

			<DividerSmall />

			<Item id={CompartmentModus.RemoveShelf}>
				<FormattedMessage defaultMessage="Verwijderen" id="mcAe/q" />
			</Item>
		</div>
	);
}

interface ItemProps {
	id: CompartmentModus;
	children: ReactNode;
	tooltip?: ReactNode;
	className?: string;
}

function Item({ id, children, tooltip, className }: ItemProps) {
	const { compartmentMode, setCompartmentMode } = useCanvasStore();

	return (
		<Switch
			className={cn("justify-between gap-4", className)}
			labelPosition="left"
			id={id.toString()}
			checked={compartmentMode === id}
			onCheckedChange={() => setCompartmentMode(compartmentMode === id ? null : id)}
		>
			{children}

			{!!tooltip && (
				<Tooltip content={tooltip} color="shades-light">
					<CircleButton component="button" className="ml-2 text-sm" />
				</Tooltip>
			)}
		</Switch>
	);
}
